import * as React from 'react';
import styles from './index.less';
import type { RcFile } from 'antd/es/upload';
import { Upload } from 'antd';
import FCropperModal from './FCropperModal';
import { FServiceAPI, FI18n, FUtil } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';

export type FUploadCoverImageType = 'jpeg' | 'png' | 'gif';

const ACCEPT_IMAGE_TYPE_CONFIG: Record<FUploadCoverImageType, { mime: string; ext: string }> = {
  jpeg: { mime: 'image/jpeg', ext: '.jpg,.jpeg,.jpe' },
  png: { mime: 'image/png', ext: '.png' },
  gif: { mime: 'image/gif', ext: '.gif' },
};

export const DEFAULT_ACCEPT_IMAGE_TYPES: FUploadCoverImageType[] = ['jpeg', 'png', 'gif'];
export const JPEG_PNG_ACCEPT_IMAGE_TYPES: FUploadCoverImageType[] = ['jpeg', 'png'];

function buildAcceptImageTypes(acceptImageTypes: FUploadCoverImageType[]) {
  return acceptImageTypes.map((type) => ACCEPT_IMAGE_TYPE_CONFIG[type].ext).join(',');
}

function getFileExtension(file: RcFile): string | undefined {
  return file.name.split('.').pop()?.toLowerCase();
}

export function isAcceptedImageFile(file: RcFile, acceptImageTypes: FUploadCoverImageType[]) {
  if (acceptImageTypes.some((type) => file.type === ACCEPT_IMAGE_TYPE_CONFIG[type].mime)) {
    return true;
  }
  const ext = getFileExtension(file);
  if (!ext) {
    return false;
  }
  if (acceptImageTypes.includes('jpeg') && ['jpg', 'jpeg', 'jpe'].includes(ext)) {
    return true;
  }
  if (acceptImageTypes.includes('png') && ext === 'png') {
    return true;
  }
  if (acceptImageTypes.includes('gif') && ext === 'gif') {
    return true;
  }
  return false;
}

export function getFormatErrorMessage(acceptImageTypes: FUploadCoverImageType[]) {
  const labels: string[] = [];
  if (acceptImageTypes.includes('jpeg')) {
    labels.push('JPEG');
  }
  if (acceptImageTypes.includes('png')) {
    labels.push('PNG');
  }
  if (acceptImageTypes.includes('gif')) {
    labels.push('GIF');
  }
  return `图片格式仅支持${labels.join('、')}`;
}

function isImageFormatErrorMsg(msg: string) {
  return msg.includes('图片格式');
}

interface FUploadCoverProps {
  use?: 'resource' | 'exhibit';
  children: React.ReactNode;
  style?: React.CSSProperties;
  uploadDescription?: string;
  /** 允许的图片类型，默认 jpg/png/gif */
  acceptImageTypes?: FUploadCoverImageType[];
  /** 禁用上传/编辑 */
  disabled?: boolean;

  onUploadSuccess?(url: string): void;

  onError?(error: string): void;

  onMouseEnter?(): void;
  onMouseLeave?(): void;
}

interface FUploadCoverStates {
  image: string;
  uploading: boolean;
}

const initStates: FUploadCoverStates = {
  image: '',
  uploading: false,
};

function FUploadCover({
  use = 'resource',
  children,
  onUploadSuccess,
  onError,
  style,
  uploadDescription,
  acceptImageTypes: acceptImageTypesProp,
  disabled = false,
  onMouseEnter,
  onMouseLeave,
}: FUploadCoverProps) {
  const acceptImageTypes = acceptImageTypesProp ?? DEFAULT_ACCEPT_IMAGE_TYPES;
  const formatErrorMessage = getFormatErrorMessage(acceptImageTypes);

  const ref = React.useRef<any>(null);
  const [$image, set$image, get$image] = FUtil.Hook.useGetState<FUploadCoverStates['image']>(
    initStates['image'],
  );
  const [$uploading, set$uploading, get$uploading] = FUtil.Hook.useGetState<
    FUploadCoverStates['uploading']
  >(initStates['uploading']);

  const notifyError = React.useCallback(
    (error: string) => {
      if (onError) {
        onError(error);
        return;
      }
      fMessage(error, 'error');
    },
    [onError],
  );

  const handleBeforeUpload = React.useCallback(
    (file: RcFile) => {
      if (!isAcceptedImageFile(file, acceptImageTypes)) {
        notifyError(formatErrorMessage);
        return false;
      }

      if (file.size > 5 * 1024 * 1024) {
        notifyError(
          // 图片大小超过限制
          FI18n.i18nNext.t('limit_resource_image_size') as string,
        );
        return false;
      }

      const reader = new FileReader();
      reader.onload = () => {
        set$image(reader.result as any);
      };
      reader.readAsDataURL(file);
      return false;
    },
    [acceptImageTypes, formatErrorMessage, notifyError, set$image],
  );

  return (
    <>
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={[styles.FUploadCover, disabled ? styles.disabled : ''].filter(Boolean).join(' ')}
        style={style}
        onClick={() => {
          if (disabled) {
            return;
          }
          ref.current.click();
        }}
      >
        {children}
      </div>
      <div style={{ display: 'none', width: 0, height: 0, overflow: 'hidden' }}>
        <Upload
          key={acceptImageTypes.join(',')}
          accept={buildAcceptImageTypes(acceptImageTypes)}
          beforeUpload={handleBeforeUpload}
          multiple={false}
          showUploadList={false}
          style={{ display: 'none' }}
        >
          <div ref={ref} className={styles.uploadContainer} />
        </Upload>
      </div>
      <FCropperModal
        use={use}
        uploading={$uploading}
        uploadRef={ref}
        imgSrc={$image}
        uploadDescription={uploadDescription}
        onOK={async (blob) => {
          const myFile = new File([blob], 'image.jpeg', {
            type: blob.type,
          });
          set$uploading(true);
          const {
            ret,
            errCode,
            msg,
            data: data_uploadImage,
          }: {
            ret: number;
            errCode: number;
            msg: string;
            data: {
              url: string;
            };
          } = await FServiceAPI.Storage.uploadImage({
            file: myFile,
          });
          await FUtil.Tool.promiseSleep(1000);
          set$uploading(false);
          if (ret !== 0 || errCode !== 0) {
            const errorMsg = isImageFormatErrorMsg(msg) ? formatErrorMessage : msg;
            notifyError(errorMsg);
            return;
          }
          onUploadSuccess && onUploadSuccess(data_uploadImage.url);
          set$image(initStates['image']);
        }}
        onCancel={() => {
          set$image(initStates['image']);
        }}
      />
    </>
  );
}

export default FUploadCover;
